import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { RegisterRequest } from '@/types/register';
import { useRegisterRequestStore } from '@/store/registerRequestStore';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { BasicInfo, FamilyInfo, AdditionalInfo } from '@/components/auth/register/components/form-sections';
import { useForm } from 'react-hook-form';
import { RegisterFormData } from '@/components/auth/register/types';

interface EditRequestDialogProps {
  request: RegisterRequest | null;
  onClose: () => void;
}

export function EditRequestDialog({ request, onClose }: EditRequestDialogProps) {
  const { updateRequest, isLoading } = useRegisterRequestStore();
  const [error, setError] = useState<string | null>(null);

  const form = useForm<RegisterFormData>({
    defaultValues: {
      name: '',
      gender: 'male',
      birthDate: null,
      maritalStatus: '',
      fatherName: '',
      grandfather1: '',
      grandfather2: '',
      grandfather3: '',
      region: '',
      occupation: '',
      branch: '',
      educationLevel: '',
      otherEducationLevel: '',
      phoneNumber: '',
      password: '',
      confirmPassword: ''
    }
  });

  // Initialize form data when request changes
  useEffect(() => {
    if (request) {
      form.reset({
        name: request.name,
        gender: request.gender,
        birthDate: request.birthDate,
        maritalStatus: request.maritalStatus,
        fatherName: request.fatherName,
        grandfather1: request.grandfather1,
        grandfather2: request.grandfather2,
        grandfather3: request.grandfather3,
        region: request.region,
        occupation: request.occupation,
        branch: request.branch,
        educationLevel: request.educationLevel,
        otherEducationLevel: request.otherEducationLevel || '',
        phoneNumber: request.phoneNumber,
        password: request.password, // Don't show password
        confirmPassword: request.password // Don't show password
      });
    }
  }, [request, form]);

  if (!request) return null;

  const handleSubmit = async (data: RegisterFormData) => {
    try {
      setError(null); // Clear previous errors
      await updateRequest(request.id, {
        name: data.name,
        gender: data.gender,
        birthDate: data.birthDate,
        maritalStatus: data.maritalStatus,
        fatherName: data.fatherName,
        grandfather1: data.grandfather1,
        grandfather2: data.grandfather2,
        grandfather3: data.grandfather3,
        region: data.region,
        occupation: data.occupation,
        branch: data.branch,
        educationLevel: data.educationLevel,
        otherEducationLevel: data.otherEducationLevel,
        phoneNumber: data.phoneNumber,
        password: request.password,
        confirmPassword: request.confirmPassword
      });
      onClose();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update request');
    }
  };

  return (
    <Dialog open={!!request} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>تعديل بيانات الطلب</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
            {/* Basic Information Section */}
            <BasicInfo form={form} />

            {/* Family Information Section */}
            <div className="border-t pt-8">
              <h3 className="text-lg font-semibold mb-6">معلومات العائلة</h3>
              <FamilyInfo form={form} />
            </div>

            {/* Additional Information Section */}
            <div className="border-t pt-8">
              <AdditionalInfo form={form} />
            </div>

            {/* Display error message if any */}
            {error && (
              <p className="text-sm text-red-500 text-center">{error}</p>
            )}

            <DialogFooter className="flex-shrink-0 border-t pt-4">
              <Button
                type="submit"
                disabled={isLoading}
                className="hover:bg-secondary"
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner className="ml-2" />
                    جاري الحفظ...
                  </>
                ) : (
                  'حفظ التعديلات'
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                disabled={isLoading}
                className="mr-2"
              >
                إلغاء
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}